import { retrieveErrors } from "@/lib/api-errors.js";

/**
 * Wraps a Pinia store action's HTTP work in the standard loading/error contract.
 *
 * The store should declare `isLoading: false` and `error: null` in state.
 * On success the result is returned; on failure `store.error` is populated
 * with a user-friendly message and the error is re-thrown so callers can
 * still react to it.
 *
 * Usage:
 *   actions: {
 *     async addItem(variantId, qty) {
 *       const { data } = await apiAction(this, () =>
 *         axios.post(CART_ENDPOINTS.relativeUpdate, { variantId, qty })
 *       );
 *       this.cart = data.cart;
 *       return data;
 *     }
 *   }
 *
 * An action that must not drive the store's main flags (e.g. a background
 * search while a form's Save button watches `isLoading`) passes its own
 * state keys: `apiAction(this, fn, { loadingKey: "isSearching", errorKey: "searchError" })`.
 */
export async function apiAction(store, fn, { loadingKey = "isLoading", errorKey = "error" } = {}) {
  store[loadingKey] = true;
  store[errorKey] = null;

  try {
    return await fn();
  } catch (errorResponse) {
    store[errorKey] = retrieveErrors(errorResponse);
    throw errorResponse;
  } finally {
    store[loadingKey] = false;
  }
}
